'use client';

import { useState } from 'react';
import { useEmailLogin } from '@/app/auth/useEmailLogin';

/**
 * Вторичный email-вход/привязка. Используется в трёх местах (EMAIL_AUTH_PLAN.md):
 *  - OnboardingModal (попап «Как начать») — variant='login', тёмная панель;
 *  - Scene11Final (финальная CTA-сцена) — variant='login', onPhoto;
 *  - VoteClient финальный экран «Вместе» — variant='claim': привязка email к
 *    анонимному аккаунту (он уже залогинен), чтобы записи сохранились навсегда.
 *
 * Дизайн (ui-ux-pro-max): email — НЕ primary CTA, он subordinate к TG/VK.
 * Тихая текст-ссылка → progressive disclosure инлайн-поля. На фото-фоне
 * (`onPhoto`) — text-shadow + scrim/blur-панель для контраста.
 *
 * variant='login' → POST /api/auth/email (вход/регистрация по email).
 * variant='claim' → POST /api/account/email-link (привязка к текущему аккаунту,
 *   next=/dashboard — после подтверждения голосующий попадает в каталог).
 */
type Variant = 'login' | 'claim' | 'create';

const VARIANTS: Record<Variant, {
  endpoint: string;
  extraBody?: Record<string, unknown>;
  linkLabel: string;
  submitLabel: string;
  success: (email: string) => React.ReactNode;
}> = {
  login: {
    endpoint: '/api/auth/email',
    linkLabel: 'Нет мессенджера? Войти по email',
    submitLabel: 'Получить ссылку на почту',
    success: (email) => (
      <>
        <span className="text-[#f5a85a]">Ссылка отправлена</span> на {email}. Проверьте почту — письмо действует 24&nbsp;часа.
      </>
    ),
  },
  claim: {
    endpoint: '/api/account/email-link',
    extraBody: { next: '/dashboard' },
    linkLabel: 'Сохранить через email',
    submitLabel: 'Отправить ссылку на почту',
    success: (email) => (
      <>
        <span className="text-[#f5a85a]">Письмо отправлено</span> на {email}. Перейди по ссылке — и записи сохранятся навсегда.
      </>
    ),
  },
  // 'create' — экран отказа в игре «Вместе»: сохранять нечего, поэтому
  // фрейминг «завести аккаунт», без слова «сохранятся».
  create: {
    endpoint: '/api/account/email-link',
    extraBody: { next: '/dashboard' },
    linkLabel: 'Завести аккаунт по email',
    submitLabel: 'Отправить ссылку на почту',
    success: (email) => (
      <>
        <span className="text-[#f5a85a]">Письмо отправлено</span> на {email}. Перейди по ссылке — и сможешь вести свой список.
      </>
    ),
  },
};

export default function EmailLoginInline({
  onPhoto = false,
  variant = 'login',
}: {
  onPhoto?: boolean;
  variant?: Variant;
}) {
  const cfg = VARIANTS[variant];
  const [expanded, setExpanded] = useState(false);
  const { email, setEmail, status, error, submit } = useEmailLogin(cfg.endpoint, cfg.extraBody);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    submit();
  }

  const photoShadow = onPhoto ? { textShadow: '0 1px 8px rgba(0,0,0,0.6)' } : undefined;

  if (status === 'sent') {
    return (
      <p className="mt-4 text-sm text-white/80 leading-relaxed" style={photoShadow} role="status" aria-live="polite">
        {cfg.success(email)}
      </p>
    );
  }

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        style={photoShadow}
        className={`mt-4 text-sm ${onPhoto ? 'text-white/70' : 'text-white/55'} hover:text-white underline underline-offset-4 decoration-white/30 hover:decoration-white/70 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 rounded`}
      >
        {cfg.linkLabel}
      </button>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      aria-label="Email"
      className={
        onPhoto
          ? 'mt-4 flex flex-col gap-2.5 max-w-sm rounded-2xl bg-black/40 backdrop-blur-sm border border-white/10 p-3'
          : 'mt-4 flex flex-col gap-2.5'
      }
    >
      <input
        type="email"
        required
        autoFocus
        autoComplete="email"
        inputMode="email"
        placeholder="email@example.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-[#f5a85a]/70 transition-colors"
      />
      <button
        type="submit"
        disabled={status === 'sending'}
        className="w-full px-4 py-3 rounded-xl border border-white/20 bg-white/5 hover:bg-white/10 disabled:opacity-60 text-white text-sm font-medium transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
      >
        {status === 'sending' ? 'Отправляем…' : cfg.submitLabel}
      </button>
      {error && <p className="text-xs text-[#f5a85a] text-center" role="alert">{error}</p>}
    </form>
  );
}
